import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DollarSign,
  Download,
  RefreshCw,
  Calendar as CalendarIcon,
  FileSpreadsheet,
  Users
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPayoutsPage = () => {
  const { getAuthHeader } = useAuth();
  const [payouts, setPayouts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate.toISOString());
      if (endDate) params.append("end_date", endDate.toISOString());
      
      const response = await axios.get(`${API}/admin/affiliate-payouts?${params}`, {
        headers: getAuthHeader()
      });
      setPayouts(response.data);
    } catch (error) {
      toast.error("Failed to load payout data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayouts();
  }, [startDate, endDate]);

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (startDate) params.append("start_date", startDate.toISOString());
      if (endDate) params.append("end_date", endDate.toISOString());
      
      const response = await axios.get(`${API}/admin/affiliate-payouts/export?${params}`, {
        headers: getAuthHeader(),
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `affiliate_payouts.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-payouts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <DollarSign className="w-7 h-7 text-primary" />
            Affiliate Payouts
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Calculate and export affiliate commissions
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchPayouts} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("csv")} className="gap-2" data-testid="export-csv-btn">
            <FileSpreadsheet className="w-4 h-4" />
            Export CSV
          </Button>
          <Button size="sm" onClick={() => handleExport("json")} className="gap-2" data-testid="export-json-btn">
            <Download className="w-4 h-4" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Date Filters */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px] justify-start text-left font-normal bg-background">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Select start"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[200px] justify-start text-left font-normal bg-background">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "Select end"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setStartDate(null); setEndDate(null); }}
            >
              Clear Dates
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {payouts && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Affiliates</p>
              <p className="text-2xl font-mono font-bold mt-1">{payouts.summary.total_affiliates}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Sales</p>
              <p className="text-2xl font-mono font-bold mt-1">${payouts.summary.total_sales.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Commission</p>
              <p className="text-2xl font-mono font-bold mt-1 text-primary">${payouts.summary.total_commission.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payouts Table */}
      <Card className="bg-card/50 border-border" data-testid="payouts-table">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Payout Breakdown by Affiliate</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="text-left px-4 py-3">Affiliate</th>
                  <th className="text-left px-4 py-3">Payment Info</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Licenses</th>
                  <th className="text-right px-4 py-3">Total Sales</th>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">Rate</th>
                  <th className="text-right px-4 py-3">Commission</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan="6" className="px-4 py-3">
                        <div className="skeleton h-10 w-full" />
                      </td>
                    </tr>
                  ))
                ) : payouts?.payouts?.length > 0 ? (
                  payouts.payouts.map((payout) => (
                    <tr key={payout.affiliate_id}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{payout.affiliate_name}</p>
                          <p className="text-xs text-muted-foreground">{payout.affiliate_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm">{payout.payment_email}</p>
                          <Badge variant="outline" className="capitalize text-xs mt-1">
                            {payout.payment_method.replace("_", " ")}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <Badge variant="outline">{payout.license_count} licenses</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        ${payout.total_sales.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right hidden sm:table-cell">
                        {payout.commission_rate}%
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono font-bold text-primary">
                          ${payout.commission_amount.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No affiliate payouts found</p>
                      <p className="text-sm">Create licenses with affiliate codes to see payout data.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-secondary/30 border-border">
        <CardContent className="p-4">
          <h4 className="font-medium mb-2">How to process payouts:</h4>
          <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
            <li>Set the date range for the payout period</li>
            <li>Export to CSV format</li>
            <li>Upload to PayPal Mass Pay, Wise Batch, or your payment processor</li>
            <li>Mark payouts as complete in your records</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPayoutsPage;
